
<template lang="pug">
  management-page.scrollable(title="Communication"
                            :creatable="false"
                            :searchable="false"
                            :showHeader="false")
    template(v-slot:body)
      aiq-row(:gutter="16" justify="end")
        .time-range-filter
          aiq-date-picker(v-model="dateRange"
                          @change="handleDatePickerFeature($event)"
                          type="daterange"
                          placeholder="Select date range",
                          popper-class="metrics-time-range__date-picker-popup"
                          :shortcuts="datePickerOptions.shortcuts"
                          :disabled-date="datePickerOptions.disabledDate"
                          :teleported="false"
                          format="YYYY/MM/DD"
                          :clearable="false")
      include ./selectableFilters/SelectableFilters.pug
      .chart-grid(v-loading="isChartLoading")
        aiq-row(:gutter="16")
          aiq-col(:span="24")
            overview-chart(v-if="configs.cobrowse_volume"
                          :dateRange="dateRangeUtc"
                          :filters="selectedFilters"
                          :chartConfig="filteredCobrowseVolumeConfig"
                          yLabel="Count"
                          :formatYTick="intCountFormatter"
                          visualization='aiq-bar-chart'
                          :reportFileName="returnReportFileName"
                          :getOriginalData="getOriginalData"
                          @onChartDataChange="onChartDataChange($event)"
                          @onChartBreakdownChange="chosenBucketOption=$event"
                          @onXLabelsChange="xLabelsCommunications=$event"
                          @onLoadingChange="isChartLoading=$event"
                          :formatXTick="xLabelsCommunicationsFormat"
                          :shouldHandlePartialDates="true"
                          :useDebounceForLoadMetrics="true"
                          )
      platform-communications-table(:dateRangeTable="getPreviousDateRange()"
                                    :breakdown="chosenBucketOption?.value || selectedBreakdownOption.value"
                                    :selectedFilterValue="getCurrentSelectedFilter()")

</template>

<script>
import { mapGetters, mapState } from 'vuex';
import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import OverviewChart from '@/components/Chart.vue';
import cobrowseVolumeConfig from '../../configs/cobrowwseVolume';
import PlatformFiltersMixin from './selectableFilters/SelectableFilters';
import PlatformCommunicationsTable from './table/CommsTable.vue';
import debounce from 'lodash/debounce';
import dayjs from '@/localPlugins/dayjs';
import { datePickerDefaultOptions, getLastQuarterRange, dstDateRangeUtc, getNonReactiveDateRange } from '../../configs/dateRange';
import cloneDeep from 'lodash/cloneDeep';

const createPickerDateRangeOption = option => ({
  text: option.label,
  value() {
    const range = [option.startTime(), option.endTime()];
    return range;
  },
});
const DATE_RANGE_OPTIONS = [
  {
    label: 'Last Month',
    startTime: () => dayjs()
      .subtract(1, 'month')
      .startOf('month'),
    endTime: () => dayjs()
      .subtract(1, 'month')
      .endOf('month'),
  },
  {
    label: 'Last Quarter',
    ...getLastQuarterRange(),
  },
];

const GREATER_THAN_90_DAYS = 90;

export default {
  pageResource: '/metrics/platform',
  name: 'Communication',
  mixins: [PlatformFiltersMixin],
  components: {
    ManagementPage,
    OverviewChart,
    PlatformCommunicationsTable,
  },
  methods: {
    /**
     * Handle date range change for Features page. If the selected date range is more than 90 days,
     * show an alert. Otherwise, update the previousDateRange and call handleDatePickerChange.
     * @param {Array<Date>} dateRange - The selected date range.
     */
    handleDatePickerFeature(dateRange) {
      dateRange = getNonReactiveDateRange(dateRange);
      const startDate = dayjs(dateRange[0]);
      const endDate = dayjs(dateRange[1]);
      this.$el.querySelector('.el-range-input').blur();
      const differenceInDays = endDate.diff(startDate, 'days');
      if (differenceInDays > GREATER_THAN_90_DAYS) {
        this.showAlert(dateRange);
      } else {
        this.previousDateRange = cloneDeep(dateRange);
        this.handleDatePickerChange(dateRange);
      }
    },

    showAlert: debounce(function(dateRange) {
      //confirm with user that date is too large
      this.$aiq.confirm(
        this.$t('metrics_tab.agents_tab.platform_tab.features_tab.date_too_large_warning_header'),
        this.$t('metrics_tab.agents_tab.platform_tab.features_tab.date_too_large_warning_body'),
        {
          confirmButtonText: this.$t('global.continue'),
        },
      ).then(() => {
        //use temp date range to hold the value of the past valid range
        //using watch fails due to the debounce implementation
        this.previousDateRange = cloneDeep(dateRange);
        this.handleDatePickerChange(dateRange);
      }).catch(() => {
        this.handleDatePickerChange(this.previousDateRange);
      });
    }, 1),

    getValidMetrics(data) {

      if (!data || !data.dataset) {
        return [];
      }

      const metricsWithData = data.dataset.filter(row => {
        return row.data_points?.some(dataPoint => {
          const countValue = dataPoint.ys?.find(y => y.metric_category === 'count')?.value;
          return countValue > 0;
        });
      });

      return metricsWithData?.map(metric => metric.metric ?? '') || [];
    },

    getFilteredData(data) {

      if (!data?.dataset) {
        return data;
      }

      const filteredData = cloneDeep(data);
      const filteredDataset = filteredData?.dataset?.filter(item => this.availableMetrics.includes(item.metric));
      filteredData.dataset = filteredDataset;

      return filteredData;
    },

    onChartDataChange(data) {

      this.availableMetrics = this.getValidMetrics(data);

      this.chartData = data?.dataset?.find(item => this.availableMetrics.includes(item.metric))?.data_points;

      // store the original data for this use case
      // on the date picker if the user picks 8/1/2024 to 8/30/2024 and chooses weekly from the granuality dropdown
      // this will result in bars for 8/1, 8/4, 8/11, 8/18, & 8/25
      // 8/1 and 8/4 will be too close to each because of how d3 renders the chart
      // we save the original data so when the user downloads the data, the dates are correct
      // for rendering the chart we change the values in the time series to 7/28, 8/4, 8/11, 8/18, & 8/25 so the bars are spaced correctly.
      // in a separate object we store the labels as 8/1, 8/4, 8/11, 8/18, & 8.25 so the x-axis values coincide with the dates chosen by the datepicker.
      // this.xLabels represents the labels for the x-axis.
      this.originalData = this.getFilteredData(data);
      this.chartData.forEach((data_point) => {
        let value = data_point.x;
        let label = dayjs(data_point.x).format('MMM DD');
        if (this.chosenBucketOption?.value === 'week') {
          value = dayjs(data_point.x).day(0).valueOf();
          // change the x value for the time series so the bars get space correctly.
          data_point.x = value;
        } else if (this.chosenBucketOption?.value === 'month') {
          label = dayjs(data_point.x).format('MMM');
        }
        this.xLabelsCommunications[value] = label;
      });
    },
    isDataPartial(date) {
      const timeStamp = new Date(date).getTime();
      const isPartial = this.chartData.find(item => item.x === timeStamp)?.isPartial;
      return isPartial;
    },
    xLabelsCommunicationsFormat(date) {
      return `${this.xLabelsCommunications[dayjs(date).valueOf()] }${this.isDataPartial(date) ? ' *' : ''}` ;
    },
  },
  watch: {
    dateRange: debounce(function onDateChange(newValue, oldValue = []) {
      const [start, end] = oldValue;
      if (!(start && end)) {
        return;
      }
      // Reload filter options for already loaded filters when date range changes
      for (const endpoint in this.loadedFilterEndpoints) {
        this.loadFilterOption(endpoint);
      }
    }, 1),
  },

  data() {
    return {
      xLabelsCommunications: {},
      cobrowseVolumeConfig,
      intCountFormatter: v => (Math.floor(parseFloat(v)) === parseFloat(v) ? v : null),
      chartData: [],
      chosenBucketOption: null,
      isChartLoading: false,
      getOriginalData: () => {
        return this.originalData;
      },
      getPreviousDateRange: () => {
        return this.previousDateRange;
      },
      getCurrentSelectedFilter: () => {
        return this.selectedFilters;
      },
      availableMetrics: [],
    };
  },
  computed: {
    ...mapGetters({
      configs: 'configs/graphsConfigs',
    }),
    ...mapState({
      showCobrowse: state => state.featureFlags.FEATURE_FLAGS.showCobrowse,
      featureFlags: state => state.featureFlags.FEATURE_FLAGS,
    }),
    datePickerOptions() {
      return {
        ...datePickerDefaultOptions(),
        shortcuts: DATE_RANGE_OPTIONS.map(createPickerDateRangeOption),
      };
    },
    dateRangeUtc() {
      return dstDateRangeUtc(this.previousDateRange);
    },
    returnReportFileName() {
      const [startTimestamp, endTimestamp] = this.dateRange;
      const startDate = dayjs.isDayjs(startTimestamp)
        ? startTimestamp.format('YYYY-MM-DD')
        : dayjs(startTimestamp).format('YYYY-MM-DD');
      const endDate = dayjs.isDayjs(endTimestamp)
        ? endTimestamp.format('YYYY-MM-DD')
        : dayjs(endTimestamp).format('YYYY-MM-DD');
      return `communication_${startDate}_${endDate}`;
    },
    filteredCobrowseVolumeConfig() {
      const filteredConfig = { ...this.cobrowseVolumeConfig };
      const filteredLines = {};

      Object.keys(this.cobrowseVolumeConfig.lines).forEach(lineKey => {
        if (this.availableMetrics.includes(lineKey)) {
          filteredLines[lineKey] = this.cobrowseVolumeConfig.lines[lineKey];
        }
      });

      filteredConfig.lines = filteredLines;
      return filteredConfig;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../styles/aiq-variables.scss";
@import "./selectableFilters/SelectableFilters.scss";

.scrollable {
  overflow-y: auto;
}

.el-card:not(:first-of-type) {
  margin-top: 24px;
}

.time-range-filter {
  position: relative;
  margin-bottom: 24px;
}


.chart-configuration {
  display: flex;
  margin-bottom: 16px;
  justify-content: space-between;
  .breakdown {
    display: flex;
    align-items: center;
    flex-grow: 1;
  }
  .members-filter {
    display: flex;
    align-items: center;
  }

  .export-command {
    display: flex;
    height: 30px;
    width: 40px;
    justify-content: center;
    align-items: center;
    margin-left: 16px;
    padding: 0;
  }
}
.footnote{
  color: #8492A6;
  font-size: 14px;
  width: 100%;
  text-align: center;
  margin-top: 20px;
}
</style>
